import useToast from '@hooks/useToast'
import { Button } from '@ui'
import { WALLET_SETUP_ROUTES } from '@utils/walletSetupRoutes'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { SetupStep } from './SetupStep'

interface SeedWord {
  index: number
  word: string
}

export const WalletSetupBackupSeed = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { setToastMessage } = useToast()

  const { t } = useTranslation()

  const words = useMemo(() => {
    const seedPhrase = location.state?.mnemonic?.split(' ') || []

    let wordsOrdered: SeedWord[] = []

    for (let i = 0; i < seedPhrase.length / 2; i++) {
      wordsOrdered.push({ index: i + 1, word: seedPhrase[i] })
      wordsOrdered.push({
        index: i + seedPhrase.length / 2 + 1,
        word: seedPhrase[i + seedPhrase.length / 2],
      })
    }

    return wordsOrdered
  }, [location.state])

  const onCopy = () => {
    setToastMessage(t('seedBackup.copied'))
    navigator.clipboard.writeText(location.state?.mnemonic || '')
  }

  const proceed = useCallback(() => {
    if (location.state?.fromSettings) {
      navigate(-1)
    } else {
      navigate(WALLET_SETUP_ROUTES.PASSWORD)
    }
  }, [navigate])

  return (
    <SetupStep
      title={t('seedBackup.title')}
      description={t('seedBackup.description')}
      // Opened from Settings it renders inside the app shell, not as a full-viewport step.
      contained={!!location.state?.fromSettings}
    >
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={onCopy}
          className="grid cursor-pointer grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-solid border-hairline bg-surface p-4 text-left transition-[background-color,transform] duration-150 hover:bg-surface-2 active:scale-[0.99] motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          {words.map((word) => (
            <span key={word.index} className="flex items-baseline gap-2">
              <span className="w-5 shrink-0 text-right font-mono text-[12px] text-fg-3 tabular">
                {word.index}
              </span>
              <span className="font-mono text-[15px] text-fg">{word.word}</span>
            </span>
          ))}
        </button>
        <Button variant="primary" fullWidth onClick={proceed}>
          I've saved these words
        </Button>
      </div>
    </SetupStep>
  )
}
